import { IUnifiedFilter } from "common247";

/**
 * Supported view mode tiers for list items.
 */
export type ThemedListViewMode = "chip" | "compact" | "medium" | "full" | "custom";

/**
 * Structured list configuration parameters supported in URL query strings.
 */
export interface IListUrlSettings {
  /** Active sort field key */
  sort?: string;
  /** Sort direction */
  order?: "asc" | "desc";
  /** Grouping criteria key */
  group?: string;
  /** Active card display view mode tier */
  view?: ThemedListViewMode;
  /** Free-text filter search query */
  q?: string;
  /** Serialized unified filter payload */
  filter?: Partial<IUnifiedFilter>;
}

/**
 * Validates and resolves a view mode parameter string to a canonical 5-tier card mode.
 *
 * @param {string} [param] - Raw query parameter value
 * @returns {ThemedListViewMode | undefined} Resolved view mode or undefined if unrecognized
 */
export function resolveListViewMode(param?: string): ThemedListViewMode | undefined {
  if (!param || typeof param !== "string") return undefined;
  const normalized = param.trim().toLowerCase();

  // Legacy mappings for backwards compatibility
  if (normalized === "small") return "compact";
  if (normalized === "details" || normalized === "large") return "full";

  if (
    normalized === "chip" ||
    normalized === "compact" ||
    normalized === "medium" ||
    normalized === "full" ||
    normalized === "custom"
  ) {
    return normalized as ThemedListViewMode;
  }

  return undefined;
}

/**
 * Strips out empty sub-objects, undefined properties, and empty arrays from a filter payload
 * to produce a clean, compact payload suitable for URL encoding.
 *
 * @param {IUnifiedFilter} filter - Filter object to prune
 * @returns {Record<string, any>} Cleaned object with only active criteria
 */
export function cleanFilterPayload(filter: IUnifiedFilter): Record<string, any> {
  const result: Record<string, any> = {};

  if (filter.searchQuery && filter.searchQuery.trim().length > 0) {
    result.searchQuery = filter.searchQuery.trim();
  }

  const childFilterKeys: (keyof IUnifiedFilter)[] = [
    "memoryFilter",
    "mediaFilter",
    "contactFilter",
    "groupFilter",
    "tagFilter",
    "shareFilter",
    "activityFilter",
    "messageFilter",
  ];

  for (const key of childFilterKeys) {
    const section = filter[key] as Record<string, any> | undefined;
    if (section && typeof section === "object") {
      const cleanSection: Record<string, any> = {};
      for (const [subKey, val] of Object.entries(section)) {
        if (val !== undefined && val !== null && val !== "" && val !== "all") {
          if (Array.isArray(val)) {
            if (val.length > 0) cleanSection[subKey] = val;
          } else {
            cleanSection[subKey] = val;
          }
        }
      }
      if (Object.keys(cleanSection).length > 0) {
        result[key] = cleanSection;
      }
    }
  }

  return result;
}

/**
 * Checks whether a unified filter has any active rules or criteria applied.
 *
 * @param {IUnifiedFilter} [filter] - Unified filter to test
 * @returns {boolean} True if the filter has active rules, false if empty/default
 */
export function isFilterActive(filter?: IUnifiedFilter): boolean {
  if (!filter) return false;
  const cleaned = cleanFilterPayload(filter);
  return Object.keys(cleaned).length > 0;
}

/**
 * Serializes an active unified filter into a URL-safe encoded string.
 * Returns undefined if no active rules are present, keeping URLs clean and minimal.
 *
 * @param {IUnifiedFilter} filter - Unified filter to serialize
 * @returns {string | undefined} URL-safe encoded string or undefined if empty
 */
export function serializeFilterToUrlParam(filter: IUnifiedFilter): string | undefined {
  const cleaned = cleanFilterPayload(filter);
  if (Object.keys(cleaned).length === 0) {
    return undefined;
  }
  try {
    return encodeURIComponent(JSON.stringify(cleaned));
  } catch {
    return undefined;
  }
}

/**
 * Safely parses and validates a serialized filter string from a URL query parameter.
 *
 * @param {string} [param] - Serialized URL query parameter
 * @returns {Partial<IUnifiedFilter> | undefined} Parsed filter criteria or undefined on error
 */
export function parseFilterFromUrlParam(param?: string): Partial<IUnifiedFilter> | undefined {
  if (!param || typeof param !== "string") return undefined;
  try {
    const decoded = decodeURIComponent(param);
    const parsed = JSON.parse(decoded);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Partial<IUnifiedFilter>;
    }
  } catch {
    // If param was already JSON decoded by router
    try {
      const direct = JSON.parse(param);
      if (direct && typeof direct === "object" && !Array.isArray(direct)) {
        return direct as Partial<IUnifiedFilter>;
      }
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Parses all standard list configuration settings from URL search parameters.
 *
 * @param {Record<string, any>} params - Route search params object from useLocalSearchParams
 * @returns {IListUrlSettings} Parsed list settings
 */
export function parseListSettingsFromParams(params: Record<string, any>): IListUrlSettings {
  const result: IListUrlSettings = {};

  if (params.sort && typeof params.sort === "string") {
    result.sort = params.sort;
  }

  if (params.order === "asc" || params.order === "desc") {
    result.order = params.order;
  }

  if (params.group && typeof params.group === "string") {
    result.group = params.group;
  }

  const resolvedView = resolveListViewMode(params.view || params.viewMode);
  if (resolvedView) {
    result.view = resolvedView;
  }

  if (params.q && typeof params.q === "string") {
    result.q = params.q;
  }

  const parsedFilter = parseFilterFromUrlParam(params.filter);
  if (parsedFilter) {
    result.filter = parsedFilter;
  }

  return result;
}

/**
 * Prepares a dictionary of query parameters to be passed to router.setParams.
 * Omits undefined or empty values so they can be cleared from the active URL.
 *
 * @param {IListUrlSettings} settings - List settings to sync
 * @returns {Record<string, string | undefined>} Parameters for router.setParams
 */
export function buildListUrlParams(settings: IListUrlSettings): Record<string, string | undefined> {
  const params: Record<string, string | undefined> = {};

  if (settings.sort !== undefined) {
    params.sort = settings.sort;
  }

  if (settings.order !== undefined) {
    params.order = settings.order;
  }

  if (settings.group !== undefined) {
    params.group = settings.group;
  }

  if (settings.view !== undefined) {
    params.view = settings.view;
  }

  if (settings.q !== undefined) {
    params.q = settings.q.trim().length > 0 ? settings.q.trim() : undefined;
  }

  if (settings.filter !== undefined) {
    params.filter = serializeFilterToUrlParam(settings.filter as IUnifiedFilter);
  }

  return params;
}
